import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import axios from 'axios';
import { getApiErrorMessage } from "./helpers/apiErrorMessage";

function Lists() {
    const [query, setQuery] = useState('');
    const [lists, setLists] = useState([]);
    const [adding, setAdding] = useState(false);
    const [warning, setWarning] = useState(false);
    const [expandedDetails, setExpandedDetails] = useState(null);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => { fetchLists(); }, []);

    const fetchLists = async () => {
        try {
            setError(null);
            const response = await axios.get(`/api/list/user`);
            setLists(response.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load your lists'));
            console.error('Error fetching lists:', error);
        } finally {
            setLoading(false);
        }
    };

    const addList = async () => {
        const newListTitle = document.getElementById(`newListTitle`).value;
        const newListDescription = document.getElementById(`newListDescription`).value;
        if (!newListTitle) { setWarning(true); return; }

        try {
            setError(null);
            await axios.post('/api/list/user', { title: newListTitle, description: newListDescription });
            await cancelAdding();
            await setWarning(false);
            await fetchLists();  // refresh the lists
        } catch (error) {
            setError(getApiErrorMessage(error, 'create a list'));
            console.error("Error adding a list: ", error.message);
        }
    };

    const handleAdding = async () => {
        setEditing(null);
        setExpandedDetails(null);
        setAdding(true);
    };

    const cancelAdding = async () => { setAdding(false); }

    if (loading) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Loading...</div>;
    }

    const handleInputChange = async (listId) => {
        const newTitle = document.getElementById(`newTitle${listId}`).value;
        const newDescription = document.getElementById(`newDescription${listId}`).value;
        if (!newTitle) { return }

        setLists(currentLists => currentLists.map(entry =>
            entry.listId === listId
                ? { ...entry, title: newTitle, description: newDescription }
                : entry
        ));

        try {
            setError(null);
            await axios.post(`/api/list/user/change`, {
                listId: listId,
                newTitle: newTitle,
                newDescription: newDescription
            });
        } catch (error) {
            setError(getApiErrorMessage(error, 'update this list'));
            console.error("Error changing user list: ", error);
        }
    }

    const handleListVisibilityChange = async (listId, isPublic) => {
        setLists(currentLists => currentLists.map(entry =>
            entry.listId === listId
                ? { ...entry, isPublic: isPublic }
                : entry
        ));

        try {
            setError(null);
            await axios.post(`/api/list/user/change`, {
                listId: listId,
                isPublic: isPublic
            });
        } catch (error) {
            setError(getApiErrorMessage(error, 'update this list'));
            console.error("Error changing list visibility: ", error);
        }
    }

    const handleDelete = async (listId) => {
        try {
            setError(null);
            await axios.delete(`/api/list/${listId}`);
            setLists(currentLists => currentLists.filter(entry => entry.listId !== listId));
            setEditing(null)
            setExpandedDetails(currentExpanded => currentExpanded === listId ? null : currentExpanded);
        } catch (error) {
            setError(getApiErrorMessage(error, 'delete this list'));
            console.error("Error deleting list: ", error);
        }
    }

    const toggleEditing = (listId) => {
        setExpandedDetails(null);
        setEditing(editing === listId ? null : listId);
    }

    const toggleDetails = (listId) => {
        setEditing(null);
        setExpandedDetails(expandedDetails === listId ? null : listId);
    };

    return (
        <div className="bg-gray-900 text-white font-sans min-h-screen">
            {error && (
                <div className="bg-red-900 border-l-4 border-red-600 p-4 m-4 rounded">
                    <div className="flex justify-between items-center">
                        <p className="text-red-200">{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-400 hover:text-red-200 text-2xl"
                        >
                            x
                        </button>
                    </div>
                </div>
            )}
            <div className="px-4 sm:px-6 md:px-10 lg:px-40">
                <div className="flex flex-col items-center w-full">
                    <div className="flex items-center w-full max-w-xl">
                        <input
                            type="text"
                            placeholder="Search List"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="flex-grow bg-gray-700 text-gray-300 p-2 mb-5 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <button className="ml-2 mb-5 p-[10px] rounded-lg fas fa-plus cursor-pointer text-white
                                           bg-green-500 hover:bg-green-600 ring-green-800 ring-1 transition-colors"
                                onClick={handleAdding}>
                        </button>
                    </div>

                    {adding && (
                        <div>
                            <div className="mb-4 text-gray-300 space-y-1 flex items-start">
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <p><strong>Title:</strong></p>
                                        <input
                                            type="text"
                                            placeholder="Title"
                                            id={`newListTitle`}
                                            className="flex-1 p-2 bg-gray-800 text-gray-300 rounded-md focus:ring focus:ring-indigo-200"
                                        />
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <p><strong>Description:</strong></p>
                                        <input
                                            type="text"
                                            placeholder="Description (optional)"
                                            id={`newListDescription`}
                                            className="flex-1 p-2 bg-gray-800 text-gray-300 rounded-md focus:ring focus:ring-indigo-200"
                                        />
                                    </div>
                                    <div className="flex space-x-2">
                                        <button
                                            className="bg-green-500 text-white p-2 rounded-lg hover:bg-green-600 transition-colors duration-200"
                                            onClick={addList}>Add
                                        </button>
                                        <button
                                            className="bg-gray-500 text-white p-2 rounded-lg hover:bg-gray-600 transition-colors duration-200"
                                            onClick={cancelAdding}>Cancel
                                        </button>
                                    </div>
                                    { warning && <p className="mt-1 text-red-600">Invalid title</p> }
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 ml-40 mr-40">
                {lists
                    .filter(entry => entry.title.toLowerCase().includes(query.toLowerCase()))
                    .map((entry, index) => (
                        <div key={entry.listId} className="flex flex-col hover:bg-gray-800 transition-colors duration-200">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center border-t border-gray-700 py-4 px-4 gap-2 sm:gap-0">
                                <Link to={`/lists/${entry.listId}`}
                                      className="flex items-center sm:ml-0 flex-grow group">
                                    <div className="relative w-[100px] h-[48px] flex-shrink-0 mr-3">
                                        <img
                                            className="absolute left-0 top-0 w-12 h-12 rounded-lg border border-gray-800 z-30 group-hover:brightness-110"
                                            src={entry.covers?.c1 || 'https://placehold.co/75x75'}
                                            alt="Top Cover"
                                        />
                                        <img
                                            className="absolute left-4 top-0 w-12 h-12 rounded-lg border border-gray-800 z-20 group-hover:brightness-110"
                                            src={entry.covers?.c2 || 'https://placehold.co/75x75'}
                                            alt="Middle Cover"
                                        />
                                        <img
                                            className="absolute left-8 top-0 w-12 h-12 rounded-lg border border-gray-800 z-10 group-hover:brightness-110"
                                            src={entry.covers?.c3 || 'https://placehold.co/75x75'}
                                            alt="Bottom Cover"
                                        />
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="font-bold text-lg">{entry.title}</span>
                                        <p className="text-gray-400 text-sm">{entry.entriesCount} entries</p>
                                    </div>
                                </Link>
                                <div className="flex mr-2">
                                    <i
                                        className={`fas fa-pencil-alt text-gray-${editing === entry.listId ? '300' : '500'} mr-2 cursor-pointer hover:text-gray-300`}
                                        onClick={() => toggleEditing(entry.listId)}
                                    ></i>
                                </div>
                                <div className="flex mr-2">
                                    <i
                                        className={`fas fa-book text-gray-${expandedDetails === entry.listId ? '300' : '500'} mr-2 cursor-pointer hover:text-gray-300`}
                                        onClick={() => toggleDetails(entry.listId)}
                                    ></i>
                                </div>
                            </div>

                            <div
                                className={`overflow-hidden transition-all duration-1000 ease-in-out ${
                                    editing === entry.listId ? 'h-48' : 'h-0'
                                }`}
                            >
                            {editing === entry.listId && (
                                    <div className="flex items-start space-x-20">
                                        <div className="flex flex-col ml-28 gap-4 p-4 rounded-lg shadow-lg w-full max-w-lg">
                                            <div className="flex items-start gap-2">
                                                <label htmlFor="title" className="text-white w-24">List title:</label>
                                                <input
                                                    type="text"
                                                    id={`newTitle${entry.listId}`}
                                                    defaultValue={entry.title}
                                                    className="flex-1 px-2 py-1 bg-gray-800 text-gray-300 rounded-md focus:ring focus:ring-indigo-200"
                                                />
                                            </div>
                                            <div className="flex items-start gap-2">
                                                <label className="text-white w-24">List description:</label>
                                                <input
                                                    type="text"
                                                    id={`newDescription${entry.listId}`}
                                                    defaultValue={entry.description}
                                                    className="flex-1 px-2 py-1 bg-gray-800 text-gray-300 rounded-md focus:ring focus:ring-indigo-200"
                                                />
                                            </div>
                                            <div className="flex items-start gap-2">
                                                <label className="text-white w-24">Is public:</label>
                                                <input
                                                    type="checkbox"
                                                    className="accent-green-500 h-5 w-5"
                                                    checked={entry.isPublic}
                                                    onChange={() => handleListVisibilityChange(entry.listId, !entry.isPublic)}
                                                />
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleInputChange(entry.listId)}
                                            className="ml-2 p-2 text-gray-500 bg-transparent rounded hover:bg-green-100 hover:text-green-600"
                                        >
                                            <i className="fas fa-check"/>
                                        </button>
                                        <button
                                            onClick={() => handleDelete(entry.listId)}
                                            className="ml-2 p-2 text-gray-500 bg-transparent rounded hover:bg-red-100 hover:text-red-600"
                                        >
                                            <i className="fas fa-trash-alt"/>
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div
                                className={`overflow-hidden transition-all duration-1000 ease-in-out ${
                                    expandedDetails === entry.listId ? 'h-48' : 'h-0'
                                }`}
                            >
                                {expandedDetails === entry.listId && (
                                    <div className="flex items-start space-x-20">
                                        <div className="ml-10 mb-4 text-gray-300 space-y-1">
                                            <p><strong>Title:</strong> {entry.title}</p>
                                            <p><strong>Description:</strong> {entry.description}</p>
                                            <p><strong>Entries count:</strong> {entry.entriesCount} entries</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    );
}

export default Lists;